import { setDatadogContextProperty } from "./datadog";

/**
 * Campaign attribution for members arriving from a lifecycle email.
 *
 * Datadog RUM already records the landing URL, so the parameters are technically
 * present on the first view. That is not enough to answer "how many members came
 * back from this email": the member lands on `/today?utm_campaign=...`, then
 * navigates, and every view after the first carries no parameters at all. Group
 * by the URL and a returning member counts as one view; group by session context
 * and they count as a member who came back and did things.
 *
 * So the parameters are lifted onto the RUM session as global context, where they
 * ride every subsequent view, action, error and Session Replay in that session.
 * `@context.utm_campaign:onboarding_welcome` then splits any existing dashboard
 * by which email drove the visit.
 *
 * Nothing here is PII: the values are our own campaign constants, set by
 * `@healthos/email`, and no member identifier travels in the link.
 */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"] as const;

/** Guards against a pathological URL turning into an unbounded facet value. */
const MAX_VALUE_LENGTH = 64;

/**
 * Read UTM parameters from a URL and tag the RUM session with them.
 *
 * Returns the campaign when one was found, so a caller can decide to do more
 * with it; returns null when this is an ordinary visit. Safe to call on every
 * boot: with no parameters present it does nothing at all.
 */
export function applyEmailAttribution(search: string = window.location.search): string | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  // Only attribute what we sent. An arbitrary utm_source from elsewhere is still
  // recorded, but a visit with no utm_medium=email is not a returning-from-email
  // visit and must not be counted as one.
  if (params.get("utm_medium") !== "email") return null;

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) setDatadogContextProperty(key, value.slice(0, MAX_VALUE_LENGTH));
  }
  return params.get("utm_campaign");
}
